define([
	"dojo",
	"dojo/has",
	"dojo/promise/all",
	"dojo/topic",
	"./Deferred",
	"./underscore"
], function(dojo, has, all, topic, Deferred, _) {

	// Package to allow tracking of multiple nested deferreds with statistics
	var _id;
	var _ctrDeferreds = 0;
	var _ctrTracking = 0;
	var _level = 0;
	var _timeStart = 0;
	var _timeServer = 0;
	var _timeImage = 0;
	var _nImages = 0;
	var _nRequests = 0;
	var _rootDeferred;
	var _callbacks;
	var _deferreds;
	var _jobs;
	var _hideTopic;
	var _messages;

	function _end() {
		// summary: end tracking of multiple deferreds
		var f = _.values(_callbacks)[0];
		var j = _jobs;

		_callbacks = null;
		_deferreds = null;
		_jobs = null;

		// process any jobs
		_.each(j, function(jf) {jf();});

		// hide progress message
		if (_hideTopic) {
			_hideTopic.remove();
			_hideTopic = null;
			_messages = null;
			topic.publish("/csi/dfd/progress");
		}

		var timeEnd = new Date().valueOf();

		console.info(_id + " (" + ((timeEnd - _timeStart) / 1000.0) + "s) - " +
			_nRequests + " requests (" + (_timeServer / 1000.0) +
			"s) - " + _nImages + " images (" + (_timeImage / 1000.0) +
			"s)");

		// notify others and call end callback
		topic.publish("/csi/dfd/end", _id, _timeStart, timeEnd, _nImages, _timeImage);

		// call callback function
		f?.();
	}

	function _refresh() {
		// summary: refresh current progress message if any - use message from latest level available
		if (!_messages) return false;

		var i, s;

		for (i = _level; i > 0; --i) {
			if (s = _messages[i]) {
				topic.publish("/csi/dfd/progress", s);
				return true;
			}
		}

		return false;
	}

	var _Tracking = {
		addJob: function(/*Function*/f, /*Deferred?*/dfd) {
			// summary: allow jobs (function) to be scheduled to occur at end of deferred tracking
			if (this.inProgress()) {
				_jobs = _jobs || [];
				_jobs.push(f);
			} else if (dfd) {
				dfd.then(f);
			} else {
				f();
			}
		},

		addImageTime: function(/*Number*/time) {
			if (_deferreds) {
				_timeImage += time;
				++_nImages;
			}
		},

		addServerTime: function(/*Number*/time) {
			if (_deferreds) {
				_timeServer += time;
				++_nRequests;
			}
		},

		allDeferreds: function() {
			// summary: return promise that is resolved when all existing deferreds (other than root) are resolved
			var dfds = _.filter(_deferreds, function(dfd) {
				return dfd != _rootDeferred;
			});

			return all(dfds);
		},

		id: function(/*String?*/id) {
			// summary: in some cases id is not known from the very start, so allow setting later
			if (id) _id = id;
			return _id;
		},

		inProgress: function() {
			return !!_deferreds;
		},

		restart: function() {
			// summary: reset start time - needed in cases where action waits for user input such as inline edits
			_timeStart = new Date().valueOf();
		},

		start: function(/*Boolean*/bForce, /*String*/id, /*String*/sMessage, /*Function*/f) {
			// if still tracking previous deferreds, then keep going
			if (!bForce && _deferreds) {
				++_ctrTracking;
				++_level;
			} else {
				_id = id;
				_ctrDeferreds = 0;
				_ctrTracking = 1;
				_level = 1;
				_timeStart = new Date().valueOf();
				_timeImage = 0;
				_timeServer = 0;
				_nImages = 0;
				_nRequests = 0;
				_jobs = null;
				_deferreds = [];
				_messages = [];

				// create one deferred to encapsulate all others; if not done, then creators of deferreds would have to make
				// sure that the next deferred in the asynchronous chain of events is created before the previous one ends.
				// This is hard to remember from a coding perspective, so to be safe have a single deferred created here to be
				// sure that reference counting does not end early.
				_rootDeferred = new Deferred();

				// display progress message for duration of deferred tracking
				this.message(_level, sMessage);
				topic.publish("/csi/dfd/start", id);
			}

			// store callback per level
			_callbacks = _callbacks || {};
			_callbacks[_level] = f;

			return _level;
		},

		message: function(/*Number*/level, /*String*/s) {
			// summary: assign progress message based on tracking level
			if (_messages) {
				_messages[level] = s;
			}

			topic.publish("/csi/dfd/progress", s);

			if (s) {
				// listen for hide message events and reinstate progress message if still valid
				_hideTopic = _hideTopic || topic.subscribe("/csi/message/hidden", _refresh);
			}
		},

		end: function(/*Number*/level) {
			if (!level) {
				return false;
			}

			var callback = _callbacks && _callbacks[level];
			var dfd = _rootDeferred;

			if (_messages) {
				delete _messages[level];
			}

			if ((--_ctrTracking == 0) && dfd) {
				// finish tracking by invoking callback on main deferred created above - this will invoke
				// remove function below so that it matches ending criteria
				_rootDeferred = null;
				dfd.resolve();
				return true;
			}

			// remove callback and call it 
			if (callback) {
				delete _callbacks[level];
				callback();
			}

			// update current tracking message since we may have switched from one progress message to another
			if (!_refresh()) {
				topic.publish("/csi/dfd/progress");
			}

			return false;
		},

		_add: function(/*Deferred*/d) {
			if (_deferreds) {
				_deferreds.push(d);
				return ++_ctrDeferreds;
			}

			return 0;
		},

		_remove: function(/*Deferred*/d) {
			// if no more instances, then stop tracking and post results
			if (!_deferreds) return;

			var x = _deferreds.dele(function(dfd) {return dfd._id == d._id;});
			if (x && !_deferreds.length) {
				_end();
			}
		}
	};

	if (has("csi-debug-deferreds")) {
		dojo.global.traceDeferreds = function() {
			if (_deferreds) {
				console.warn("Deferreds: " + _deferreds.length);

				_.each(_deferreds, function(d, i) {
					console.warn(i + ": " + d._id);
					console.log(d._debugErr.stack);
				});

				console.warn("------------------------------------");
			} else {
				console.info("Deferreds: 0");
			}
		};
	}

	// register with Deferred to prevent circular reference
	Deferred.prototype._tracking = _Tracking;

	return _Tracking;
});